import { ReactNode, Ref } from "react"

import { IconAlertTriangleFilled, IconInfoCircle } from "@tabler/icons-react"

import { cn } from "@/lib/utils"

import { Tooltip } from "./Tooltip"

export interface FormFieldProps {
  label?: ReactNode
  id?: string
  helperText?: ReactNode
  errorText?: string
  required?: boolean
  disabled?: boolean
  children: ReactNode
  className?: string
  length?: number
  maxLength?: number
  withCharCounter?: boolean
  hideOptionalLabel?: boolean
  vertical?: number | boolean
  labelClassName?: string
  tooltip?: ReactNode
  ref?: Ref<HTMLDivElement>
}

export const FormField = ({
  label,
  id,
  helperText,
  errorText,
  required,
  disabled,
  children,
  className,
  length,
  maxLength,
  withCharCounter,
  hideOptionalLabel,
  vertical,
  labelClassName,
  tooltip,
  ref,
}: FormFieldProps) => {
  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col gap-1",
        { "flex-row items-start gap-4": vertical },
        className,
      )}
    >
      {label || helperText || (!vertical && !required && !hideOptionalLabel) ? (
        <div
          className={cn("flex flex-col justify-center", {
            hidden: !label && !(helperText || !required),
            "min-h-10": vertical,
          })}
          style={vertical ? { width: vertical === true ? 120 : vertical } : undefined}
        >
          {label && (
            <label
              htmlFor={id}
              className={cn(
                "gap-2 text-body2 text-muted-foreground",
                "flex items-center",
                {
                  "text-muted-foreground/70": disabled,
                },
                labelClassName,
              )}
            >
              {label} {vertical && required ? "*" : null}
              {tooltip && (
                <Tooltip content={tooltip} className="max-w-sm" disableHoverablePopup>
                  <IconInfoCircle size={14} className="shrink-0" />
                </Tooltip>
              )}
            </label>
          )}
          {(!vertical && !required && !hideOptionalLabel) || helperText ? (
            <span
              className={cn(
                "text-caption text-muted-foreground/90",
                {
                  "text-muted-foreground/60": disabled,
                },
                labelClassName,
              )}
            >
              {!required && !hideOptionalLabel && !vertical ? "(Optional) " : ""}
              {helperText}
            </span>
          ) : null}
        </div>
      ) : null}
      <div className={cn("flex min-w-0 grow basis-0 flex-col gap-2")}>
        {children}
        {errorText || withCharCounter ? (
          <div className="flex justify-between">
            <span
              className={cn(
                "flex items-center gap-1 text-body2 text-destructive",
                labelClassName,
              )}
            >
              {errorText ? (
                <>
                  <IconAlertTriangleFilled /> {errorText}
                </>
              ) : null}
            </span>
            {withCharCounter && maxLength ? (
              <FormFieldCounter length={length} maxLength={maxLength} />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function FormFieldCounter({
  length = 0,
  maxLength,
}: {
  length?: number
  maxLength: number
}) {
  const lengthExceeded = length > maxLength

  return (
    <span
      className={cn("text-body2 text-muted-foreground", {
        "text-destructive": lengthExceeded,
      })}
    >
      {length}/{maxLength}
    </span>
  )
}
